/**
 * Aba do Mercado Livre: resumo do canal, produtos × anúncios e anúncios (com conciliação).
 * Só leitura: os dados vêm da última leitura dos anúncios feita pelo worker.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, Download, ExternalLink, Link2, Pencil, RefreshCw, Search, Unlink, X } from 'lucide-react';
import type {
  ChannelSettingsResponse,
  CategoryCount,
  ChannelInfo,
  ChannelListingRow,
  ChannelProductRow,
  ChannelSummary,
  BulkStatusResult,
  LinkStatus,
  ListingAlert,
  Page,
  ProductRow,
} from '@berytus/contracts';
import { api, brl, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag, Thumb, buyBoxTag } from '../components/ui.tsx';
import { titleOf } from './Catalog.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { PublishDialog, PRE_ALERT } from './Publish.tsx';
import { ListingEditor } from './ListingEditor.tsx';
import { ListingCategorySelect, categoryParams } from '../components/ListingCategory.tsx';
import { BrandActionBar, BrandFilterInput } from '../components/BrandFilter.tsx';
import { DuplicatesButton } from '../components/Duplicates.tsx';
import { ChannelHome } from './ChannelHome.tsx';
import { AttrFilterButton } from '../components/AttrFilter.tsx';
import { pickAccount, useSelectedAccountId } from '../components/AccountPicker.tsx';
import { can } from '@berytus/contracts';


const LISTING_STATUS: Record<string, { label: string; tone: string }> = {
  active: { label: 'Ativo', tone: 'ok' },
  paused: { label: 'Pausado', tone: 'warn' },
  closed: { label: 'Finalizado', tone: 'off' },
  under_review: { label: 'Em revisão', tone: 'warn' },
  inactive: { label: 'Inativo', tone: 'off' },
};
/** Sub-status do Mercado Livre que interessam na lista (moderação e falta de estoque). */
const SUB_STATUS: Record<string, { label: string; tone: string }> = {
  forbidden: { label: 'proibido', tone: 'err' }, held: { label: 'retido', tone: 'err' }, suspended: { label: 'suspenso', tone: 'err' },
  freezed: { label: 'congelado', tone: 'err' }, banned: { label: 'banido', tone: 'err' }, waiting_for_patch: { label: 'aguardando correção', tone: 'warn' },
  under_review: { label: 'em revisão', tone: 'warn' }, out_of_stock: { label: 'sem estoque', tone: 'off' },
};
const subTags = (sub: string[] | null | undefined) => (sub ?? []).filter((x) => SUB_STATUS[x]).map((x) => <Tag key={x} tone={SUB_STATUS[x]!.tone}>{SUB_STATUS[x]!.label}</Tag>);
const statusTag = (s: string | null) => {
  const st = (s && LISTING_STATUS[s]) || { label: s ?? '—', tone: 'off' };
  return <Tag tone={st.tone}>{st.label}</Tag>;
};

const LINK: Record<LinkStatus, { label: string; tone: string; color: string }> = {
  linked: { label: 'Vinculado', tone: 'ok', color: 'var(--ok)' },
  ambiguous: { label: 'Revisar', tone: 'warn', color: 'var(--warn)' },
  orphan: { label: 'Órfão', tone: 'err', color: 'var(--err)' },
  pending: { label: 'Pendente', tone: 'off', color: 'var(--line-strong)' },
  ignored: { label: 'Ignorado', tone: 'off', color: 'var(--line-strong)' },
};
const METHOD: Record<string, string> = { seller_sku: 'pelo SKU', gtin: 'pelo EAN', manual: 'manual', title: 'pelo título' };

const useSummary = () =>
  useQuery({
    queryKey: ['meli', 'summary'],
    queryFn: () => api<ChannelSummary>('/channels/mercadolivre/summary'),
    refetchInterval: (q) => (q.state.data?.lastPull && ['queued', 'running'].includes(q.state.data.lastPull.status) ? 4000 : false),
  });

export function Head({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Resumo

export function MeliSummary() {
  const q = useSummary();
  const channels = useQuery({ queryKey: ['channels'], queryFn: () => api<ChannelInfo[]>('/channels') });
  const account = channels.data?.find((c) => c.code === 'mercadolivre')?.accounts.find((a) => a.status !== 'revoked');
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ['meli', 'settings', account?.id],
    queryFn: () => api<ChannelSettingsResponse>(`/channels/accounts/${account!.id}/settings`),
    enabled: !!account,
  });
  const pull = useMutation({
    mutationFn: () => api(`/channels/accounts/${account!.id}/pull`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meli'] });
      qc.invalidateQueries({ queryKey: ['channels'] });
    },
  });

  if (q.isPending) return <><Head title="Mercado Livre" /><Loading /></>;
  if (q.error) return <><Head title="Mercado Livre" /><ErrorBox error={q.error} /></>;
  const s = q.data;
  const running = s.lastPull && ['queued', 'running'].includes(s.lastPull.status);
  const items = typeof s.lastPull?.stats?.items === 'number' ? (s.lastPull.stats.items as number) : null;
  const review = s.link.ambiguous + s.link.orphan;

  const syncLine = settings.data && (() => {
    const st = settings.data.settings;
    const mode = { off: 'desligado', stock: 'só estoque', price: 'só preço', price_stock: 'preço e estoque' }[st.syncMode];
    return <> · Envio de preço e estoque: <Link to="/canal/mercadolivre/configuracoes">{mode}</Link>, {int(st.stockPct)}% do estoque
      {st.lastSyncAt ? `, último em ${dateTime(st.lastSyncAt)}${st.lastSyncStats?.sent ? ` (${int(st.lastSyncStats.sent)} alterados)` : ''}` : ''}</>;
  })();

  // Cards de anúncios (uma seção só; o vínculo com o catálogo entra em "Para revisar" e "Produtos anunciados").
  const listings = (
    <div className="kpis">
      <Link className="kpi" to="/canal/mercadolivre/anuncios?status=active">
        <span className="k-label">Anúncios ativos</span>
        <span className="k-value">{int(s.listings.active)}</span>
        <span className="k-note">{int(s.catalogListings)} no catálogo do Mercado Livre</span>
      </Link>
      <Link className="kpi" to="/canal/mercadolivre/anuncios?status=paused">
        <span className="k-label">Anúncios pausados</span>
        <span className="k-value">{int(s.listings.paused)}</span>
        <span className="k-note">{int(s.listings.closed)} finalizados · {int(s.listings.other)} em outras situações</span>
      </Link>
      <Link className={`kpi${review > 0 ? ' alert' : ''}`} to="/canal/mercadolivre/conciliar">
        <span className="k-label">Anúncios para revisar</span>
        <span className="k-value">{int(review)}</span>
        <span className="k-note">{int(s.link.orphan)} órfãos · {int(s.link.ambiguous)} ambíguos · {int(s.link.pending)} pendentes · {int(s.link.linked)} vinculados</span>
      </Link>
      <Link className="kpi" to="/canal/mercadolivre/produtos?has=with">
        <span className="k-label">Produtos anunciados</span>
        <span className="k-value">{int(s.products.withListing)}</span>
        <span className="k-note">
          de {int(s.products.active)} SKUs ativos ({((100 * s.products.withListing) / (s.products.active || 1)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)
        </span>
      </Link>
      <Link className={`kpi${s.products.withoutListingInStock > 0 ? ' alert' : ''}`} to="/canal/mercadolivre/produtos?has=without&stock=in">
        <span className="k-label">Com estoque e sem anúncio</span>
        <span className="k-value">{int(s.products.withoutListingInStock)}</span>
        <span className="k-note">{int(s.products.withoutListing)} SKUs ativos sem anúncio (faltam anunciar)</span>
      </Link>
    </div>
  );

  return (
    <>
      <Head title={`Mercado Livre${account?.displayName ? ` · ${account.displayName}` : ''}`}>
        <p style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            {running
              ? `Lendo os anúncios da conta${items ? ` (${int(items)} até agora)` : ''}…`
              : s.lastPull?.finishedAt
                ? `Anúncios lidos em ${dateTime(s.lastPull.finishedAt)} (a leitura completa se repete a cada 6 horas; mudanças chegam antes, pelas notificações)`
                : 'Os anúncios ainda não foram lidos'}
            {syncLine}
          </span>
          <button className="btn sm" type="button" disabled={!account || account.status !== 'active' || !!running || pull.isPending} onClick={() => pull.mutate()}>
            <RefreshCw size={14} /> {running ? 'Lendo…' : 'Ler anúncios agora'}
          </button>
        </p>
      </Head>
      {pull.error && <div style={{ marginBottom: 12 }}><ErrorBox error={pull.error} /></div>}

      {account ? (
        <ChannelHome channel="mercadolivre" listings={listings} notices={<>
          {account.status === 'expired' && (
            <div className="notice err">A autorização da conta venceu ou foi revogada no Mercado Livre. <Link to="/canais">Reconectar</Link>.</div>
          )}
          {s.lastPull?.status === 'failed' && <div className="notice err">A última leitura falhou: {s.lastPull.error}</div>}
        </>} />
      ) : (
        <div className="stack-y">
          {s.lastPull?.status === 'failed' && <div className="notice err">A última leitura falhou: {s.lastPull.error}</div>}
          {listings}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Utilitários de lista

export function useUrlState() {
  const [params, setParams] = useSearchParams();
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v == null || v === '' ? next.delete(k) : next.set(k, v));
    setParams(next, { replace: true });
  };
  return [params, set] as const;
}

/** Busca com atraso, para não consultar a cada tecla. */
export function useSearchBox(params: URLSearchParams, set: (p: Record<string, string | null>) => void) {
  const [search, setSearch] = useState(params.get('q') ?? '');
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get('q') ?? '') !== search.trim()) set({ q: search.trim() || null, page: null });
    }, 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  return [search, setSearch] as const;
}

export function Pager({ total, page, pageSize, onPage }: { total: number; page: number; pageSize: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;
  return (
    <div className="grid-foot">
      <span className="tnum">{int((page - 1) * pageSize + 1)}–{int(Math.min(page * pageSize, total))} de {int(total)}</span>
      <div className="pager">
        <button className="btn sm" type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Página anterior"><ChevronLeft size={16} /></button>
        <span className="tnum">{int(page)} / {int(pages)}</span>
        <button className="btn sm" type="button" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Próxima página"><ChevronRight size={16} /></button>
      </div>
    </div>
  );
}

const yesNo = (v: boolean) => (v ? <span className="yes">Sim</span> : <span className="no">Não</span>);

// ---------------------------------------------------------------------------------------------
// Produtos × anúncios

// Largura 0 = coluna elástica (ocupa o que sobrar da tela).
const PRODUCT_COLS: [string, number, string?][] = [
  ['', 40], ['SKU', 100], ['Nome', 0], ['Categoria', 130], ['Catálogo ML', 150], ['Anúncio', 130], ['Anúncio no Mercado Livre', 0],
  ['Preço ML', 100, 'num'], ['Estoque ML', 120, 'num'], ['Disponível', 120, 'num'],
];
const minWidth = (cols: [string, number, string?][]) => cols.reduce((n, c) => n + (c[1] || 160), 0);

const CATALOG_TAG: Record<string, { label: string; tone: string }> = {
  found: { label: 'No catálogo', tone: 'ok' },
  parent: { label: 'Ficha com variações', tone: 'warn' },
  not_found: { label: 'Não encontrado', tone: 'off' },
  error: { label: 'Erro na consulta', tone: 'err' },
};

/** Filtros da tela para a planilha (sem paginação). */
const exportQuery = (q: URLSearchParams) => {
  const out = new URLSearchParams(q);
  out.delete('page'); out.delete('pageSize');
  return out.toString();
};

export function MeliProducts() {
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const admin = can(useMe().tenant.access, 'products_listings', 'write');
  const { account } = useMeliAccount();
  const page = Number(params.get('page') ?? 1);
  const query = new URLSearchParams(params);
  query.set('pageSize', '50');
  if (account) query.set('accountId', account.id);
  const summary = useSummary();
  const lookupRunning = !!summary.data?.catalog.lastLookup && ['queued', 'running'].includes(summary.data.catalog.lastLookup.status);
  const list = useQuery({
    queryKey: ['meli', 'products', query.toString()],
    queryFn: () => api<Page<ChannelProductRow>>(`/channels/mercadolivre/products?${query}`),
    placeholderData: keepPreviousData,
    refetchInterval: lookupRunning ? 8000 : false,
  });
  const cats = useQuery({ queryKey: ['categories'], queryFn: () => api<CategoryCount[]>('/catalog/categories') });
  const lookup = useMutation({
    mutationFn: () => api(`/channels/accounts/${account!.id}/catalog-lookup`, { method: 'POST', json: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meli'] }),
  });
  const rows = list.data?.items ?? [];
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [publishing, setPublishing] = useState<{ productIds?: string[]; filter?: { category?: string; attrs?: string; alert?: string; stock?: 'in' } } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  useEffect(() => setSelected(new Set()), [query.toString()]); // eslint-disable-line react-hooks/exhaustive-deps
  const canPublish = (p: ChannelProductRow) => p.catalog?.status === 'found' && !p.main; // kit: anúncio próprio
  const toggle = (id: string) => setSelected((cur) => {
    const n = new Set(cur);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const cat = summary.data?.catalog;

  return (
    <>
      <Head title="Produtos × anúncios">
        <p>
          Cada produto ativo e o seu anúncio no Mercado Livre. Produto com ficha no catálogo do ML e sem anúncio
          pode ser anunciado daqui, um a um ou em massa. Os anúncios nascem pausados.
        </p>
      </Head>

      <div className="notice small" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        {cat ? (
          <span>
            Catálogo do ML: <b>{int(cat.found)}</b> produtos encontrados · <b>{int(cat.foundUnlisted)}</b> prontos para anunciar ·{' '}
            {int(cat.notFound)} não encontrados · {int(cat.unchecked)} não consultados
            {lookupRunning && cat.lastLookup?.stats && typeof cat.lastLookup.stats.done === 'number' && (
              <> · consultando {int(cat.lastLookup.stats.done as number)} de {int(cat.lastLookup.stats.total as number)}…</>
            )}
          </span>
        ) : <span>Carregando…</span>}
        <span style={{ flex: 1 }} />
        {cat && cat.foundUnlisted > 0 && admin && (
          <button className="btn sm primary" type="button" onClick={() => set({ catalog: 'ready', has: null, page: null })}>Ver prontos para anunciar</button>
        )}
        {account && (
          <button className="btn sm" type="button" disabled={lookupRunning || lookup.isPending} onClick={() => lookup.mutate()}>
            <RefreshCw size={14} /> {lookupRunning ? 'Consultando o catálogo…' : 'Consultar catálogo do ML'}
          </button>
        )}
      </div>
      {lookup.error && <div style={{ marginBottom: 12 }}><ErrorBox error={lookup.error} /></div>}

      <div className="toolbar">
        <label className="search">
          <Search size={16} />
          <span className="sr-only">Buscar</span>
          <input className="input" type="search" placeholder="Buscar por SKU, EAN ou nome" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <select className="select" aria-label="Categoria" value={params.get('category') ?? ''} onChange={(e) => set({ category: e.target.value || null, page: null })}>
          <option value="">Todas as categorias</option>
          {cats.data?.map((c) => <option key={c.category} value={c.category}>{c.category}</option>)}
        </select>
        <AttrFilterButton params={params} set={set} />
        <select className="select" aria-label="Catálogo do ML" value={params.get('catalog') ?? 'all'} onChange={(e) => set({ catalog: e.target.value === 'all' ? null : e.target.value, page: null })}>
          <option value="all">Catálogo ML: todos</option>
          <option value="ready">Prontos para anunciar</option>
          <option value="found">Encontrados no catálogo</option>
          <option value="not_found">Não encontrados</option>
          <option value="unchecked">Não consultados</option>
        </select>
        <div className="seg" role="group" aria-label="Anúncio">
          {([['all', 'Todos'], ['with', 'Com anúncio'], ['without', 'Sem anúncio']] as const).map(([v, l]) => (
            <button key={v} type="button" aria-pressed={(params.get('has') ?? 'all') === v} onClick={() => set({ has: v === 'all' ? null : v, page: null })}>{l}</button>
          ))}
        </div>
        <select className="select" aria-label="Alertas" value={params.get('alert') ?? ''} onChange={(e) => set({ alert: e.target.value || null, page: null })}>
          <option value="">Alertas: todos</option>
          <option value="none">Sem alerta (pronto para publicar)</option>
          <option value="any">Com algum alerta</option>
          <option value="title_any">Ficha diferente do produto</option>
          <option value="review">Anúncio em revisão / inativo</option>
          <option value="same_sku">SKU já anunciado sem vínculo</option>
          <option value="failed">Última publicação recusada</option>
          <option value="price">Preço ou estoque (margem, custo)</option>
        </select>
        <label className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" className="check" checked={params.get('stock') === 'in'} onChange={(e) => set({ stock: e.target.checked ? 'in' : null, page: null })} />
          Só com estoque
        </label>
        <a className="btn" href={`/api/v1/channels/mercadolivre/products/export.csv?${exportQuery(query)}`}
          title="Planilha (abre no Excel) com os produtos e anúncios deste filtro">
          <Download size={16} /> Exportar
        </a>
      </div>

      <div className="panel flush">
        {admin && (selected.size > 0 || (params.get('catalog') === 'ready' && (list.data?.total ?? 0) > 0)) && (
          <div className="bulkbar" role="status">
            {selected.size > 0 ? (
              <>
                <b>{selected.size} selecionado{selected.size > 1 ? 's' : ''}</b>
                <button className="btn sm primary" type="button" onClick={() => setPublishing({ productIds: [...selected] })}>Anunciar selecionados</button>
                <button className="btn sm quiet" type="button" onClick={() => setSelected(new Set())}><X size={14} /> Limpar</button>
              </>
            ) : (
              <>
                <span><b>{int(list.data!.total)}</b> prontos para anunciar{params.get('category') ? ` em ${params.get('category')}` : ''}{params.get('alert') === 'none' ? ' sem alerta' : ''}</span>
                <button className="btn sm primary" type="button" onClick={() => setPublishing({ filter: {
                  category: params.get('category') ?? undefined, attrs: params.get('attrs') ?? undefined,
                  alert: params.get('alert') ?? undefined, stock: params.get('stock') === 'in' ? 'in' : undefined,
                } })}>
                  Anunciar todos{params.get('category') || params.get('attrs') || params.get('alert') || params.get('stock') ? ' deste filtro' : ''}
                </button>
              </>
            )}
          </div>
        )}
        {list.error ? <div style={{ padding: 18 }}><ErrorBox error={list.error} /></div>
          : !list.isPending && rows.length === 0 ? <div className="empty"><b>Nenhum produto encontrado</b>Mude a busca ou os filtros.</div>
          : (
            <>
              <div className="grid-wrap" style={{ opacity: list.isPlaceholderData ? 0.55 : 1 }}>
                <table className="grid" style={{ width: '100%', minWidth: minWidth(PRODUCT_COLS) }}>
                  <colgroup>{PRODUCT_COLS.map(([, w], i) => <col key={i} style={w ? { width: w } : undefined} />)}</colgroup>
                  <thead>
                    <tr>
                      {PRODUCT_COLS.map(([l, , c], i) => (
                        <th key={i} className={c}>
                          {i === 0 ? (
                            <input type="checkbox" className="check" aria-label="Selecionar a página" checked={allOnPage}
                              onChange={() => setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r.id)))} />
                          ) : l}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {list.isPending
                      ? Array.from({ length: 10 }, (_, i) => <tr key={i}>{PRODUCT_COLS.map((_, j) => <td key={j}><div className="skeleton" /></td>)}</tr>)
                      : rows.map((p) => (
                          <tr key={p.id} className={selected.has(p.id) ? 'selected' : undefined} onClick={() => navigate(`/catalogo/${p.id}`)}>
                            <td onClick={(e) => e.stopPropagation()}>
                              <input type="checkbox" className="check" aria-label={`Selecionar ${p.sku}`} checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                            </td>
                            <td>
                              <div className="cell-2">
                                <span className="code">{p.sku}</span>
                                {p.gtin && <span className="sub">{p.gtin}</span>}
                              </div>
                            </td>
                            <td title={p.title ?? undefined}><span className={p.title ? '' : 'untitled'}>{titleOf(p)}</span>{p.isKit && <> <Tag tone="info">Kit {p.kitUnits} un.</Tag></>}</td>
                            <td>{p.category ?? '—'}</td>
                            <td title={p.catalog?.name ?? undefined}>
                              {p.catalog ? (
                                <div className="cell-2">
                                  <span><Tag tone={CATALOG_TAG[p.catalog.status]!.tone}>{CATALOG_TAG[p.catalog.status]!.label}</Tag></span>
                                  {p.catalog.catalogProductId && (
                                    <span className="sub" title={p.catalog.gtin && p.catalog.gtin !== p.gtin ? `Encontrado pelo EAN ${p.catalog.gtin}` : undefined}>
                                      {p.catalog.catalogProductId}{p.catalog.gtin && p.catalog.gtin !== p.gtin && ' · outro EAN'}
                                    </span>
                                  )}
                                </div>
                              ) : <span className="small muted">não consultado</span>}
                              {!!p.alerts?.length && (
                                <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                                  {p.alerts.map((a) => <span key={a.code} title={a.detail}><Tag tone={PRE_ALERT[a.code]?.tone ?? 'warn'}>{PRE_ALERT[a.code]?.label ?? a.code}</Tag></span>)}
                                </span>
                              )}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              {p.main ? (
                                <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                                  {statusTag(p.main.status)}{p.listings > 1 && <span className="small muted"> +{p.listings - 1}</span>}
                                  <button className="icon-btn" style={{ width: 28, height: 28 }} type="button" onClick={() => setEditing(p.main!.listingId)} aria-label={`Editar anúncio ${p.main.externalId}`} title="Editar anúncio">
                                    <Pencil size={14} />
                                  </button>
                                </span>
                              )
                                : canPublish(p) && admin ? (
                                  <button className="btn sm primary" type="button" onClick={() => setPublishing({ productIds: [p.id] })}>Anunciar</button>
                                ) : <Tag tone="off">Sem anúncio</Tag>}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              {p.main ? (
                                <div className="cell-2">
                                  <span title={p.main.title ?? undefined}>{p.main.title}</span>
                                  <span className="sub">
                                    {p.main.permalink ? <a className="ext" href={p.main.permalink} target="_blank" rel="noreferrer">{p.main.externalId} <ExternalLink size={11} /></a> : p.main.externalId}
                                    {p.main.isCatalog && ' · catálogo'}
                                  </span>
                                </div>
                              ) : p.catalog?.name ? <span className="small muted" title={p.catalog.name}>{p.catalog.name}</span> : '—'}
                            </td>
                            <td className="num">{p.main ? brl(p.main.price) : '—'}</td>
                            <td className="num">{p.main?.availableQuantity != null ? int(p.main.availableQuantity) : '—'}</td>
                            <td className="num">{int(p.available)}</td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
              <div className="cards">
                {rows.map((p) => (
                  <Link key={p.id} to={`/catalogo/${p.id}`} className="card-row" style={{ gridTemplateColumns: '1fr auto' }}>
                    <div style={{ minWidth: 0 }}>
                      <div className={`t${p.title ? '' : ' untitled'}`}>{titleOf(p)}</div>
                      <div className="m">SKU {p.sku}{p.gtin && <> · {p.gtin}</>}</div>
                      <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
                        {p.main ? statusTag(p.main.status) : <Tag tone="off">Sem anúncio</Tag>}
                        {p.catalog && <Tag tone={CATALOG_TAG[p.catalog.status]!.tone}>{CATALOG_TAG[p.catalog.status]!.label}</Tag>}
                      </div>
                    </div>
                    <div className="r">
                      <div>{p.main ? brl(p.main.price) : '—'}</div>
                      <div className="muted">{int(p.available)} disp.</div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        {list.data && <Pager total={list.data.total} page={page} pageSize={list.data.pageSize} onPage={(n) => set({ page: n > 1 ? String(n) : null })} />}
      </div>
      {account && publishing && (
        <PublishDialog accountId={account.id} target={publishing} onClose={() => setPublishing(null)} onDone={() => setSelected(new Set())} />
      )}
      {editing && <ListingEditor listingId={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Anúncios e conciliação

const LISTING_COLS: [string, number, string?][] = [
  ['', 40], ['', 44], ['', 56], ['Anúncio', 0], ['SKU', 112], ['Situação', 104], ['Preço', 112, 'num'], ['Estoque ML', 120, 'num'], ['Estoque Berytus', 172, 'num'],
  ['Buy box', 190], ['Alertas', 150], ['Vínculo', 112], ['Produto na Berytus', 280],
];

export const LISTING_ALERT: Record<ListingAlert, { label: string; tone: string }> = {
  below_cost: { label: 'Abaixo do custo', tone: 'err' },
  above_pmc: { label: 'Acima do PMC', tone: 'err' },
  low_margin: { label: 'Margem baixa', tone: 'warn' },
  buybox_lost: { label: 'Perdendo buy box', tone: 'err' },
  price_out_of_sync: { label: 'Diferente da tabela', tone: 'info' },
  stock_out_of_sync: { label: 'Estoque diferente', tone: 'info' },
  title_mismatch: { label: 'Título diferente', tone: 'err' },
  kit_mismatch: { label: 'Kit × unidade', tone: 'err' },
  measure_mismatch: { label: 'Medida diferente', tone: 'warn' },
};
export const TITLE_ALERTS = new Set<ListingAlert>(['title_mismatch', 'kit_mismatch', 'measure_mismatch']);

export function MeliListings({ review = false }: { review?: boolean }) {
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const qc = useQueryClient();
  const page = Number(params.get('page') ?? 1);
  const query = new URLSearchParams(params);
  query.set('pageSize', '50');
  if (review && !params.get('link')) query.set('link', 'review');
  const list = useQuery({
    queryKey: ['meli', 'listings', query.toString()],
    queryFn: () => api<Page<ChannelListingRow>>(`/channels/mercadolivre/listings?${query}`),
    placeholderData: keepPreviousData,
  });
  const applyBuyBox = useMutation({
    mutationFn: (id: string) => api(`/channels/listings/${id}/apply-buybox`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meli'] }),
  });
  const link = useMutation({
    mutationFn: ({ id, productId }: { id: string; productId: string | null }) =>
      api(`/channels/listings/${id}/link`, { method: 'PUT', json: { productId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meli'] }),
  });
  const [picking, setPicking] = useState<ChannelListingRow | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const titleOk = useMutation({
    mutationFn: (id: string) => api(`/channels/listings/${id}/title-ok`, { method: 'POST', json: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meli'] }),
  });
  const rows = list.data?.items ?? [];
  const linkValue = params.get('link') ?? (review ? 'review' : 'all');
  const me = useMe();
  const canEdit = can(me.tenant.access, review ? 'reconcile' : 'listings', 'write') && !me.impersonating;
  const { account } = useMeliAccount();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => setSelected(new Set()), [query.toString()]); // eslint-disable-line react-hooks/exhaustive-deps
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggle = (id: string) => setSelected((cur) => {
    const n = new Set(cur);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const bulk = useMutation({
    mutationFn: (status: 'active' | 'paused' | 'closed' | 'deleted') => api<BulkStatusResult>('/channels/listings/bulk-status', { method: 'POST', json: { ids: [...selected], status } }),
    onSuccess: () => { setSelected(new Set()); qc.invalidateQueries({ queryKey: ['meli'] }); },
  });
  const pull = useMutation({
    mutationFn: () => api(`/channels/accounts/${account!.id}/pull`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meli'] }),
  });
  // Todos do filtro (não só a página): vai para a fila do worker; o resultado de cada um aparece no anúncio.
  const bulkAll = useMutation({
    mutationFn: (status: 'active' | 'paused') => {
      const filter = Object.fromEntries([...query.entries()].filter(([k]) => !['page', 'pageSize'].includes(k)));
      return api<{ queued: number }>('/channels/mercadolivre/listings/bulk-filter', { method: 'POST', json: { status, filter } });
    },
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ['meli'] }), 5000),
  });
  const total = list.data?.total ?? 0;
  const canAll = canEdit && selected.size === 0 && total > 0 && ['active', 'paused'].includes(params.get('status') ?? '');

  return (
    <>
      <Head title={review ? 'Conciliar vínculos' : 'Anúncios'}>
        <p>
          {review
            ? 'Anúncios que a Berytus não conseguiu ligar a um produto com certeza. Aceite a sugestão ou escolha o produto; vínculos feitos aqui não são desfeitos nas próximas leituras.'
            : 'Todos os anúncios da conta, como estão no Mercado Livre, e o produto da Berytus a que cada um está ligado.'}
        </p>
      </Head>
      <div className="toolbar">
        <label className="search">
          <Search size={16} />
          <span className="sr-only">Buscar</span>
          <input className="input" type="search" placeholder="Buscar por MLB, SKU, EAN ou título" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <BrandFilterInput channel="mercadolivre" accountId={account?.id} params={params} set={set} />
        <ListingCategorySelect channel="mercadolivre" params={params} set={set} />
        <AttrFilterButton params={params} set={set} />
        <select className="select" aria-label="Situação" value={params.get('status') ?? 'all'} onChange={(e) => set({ status: e.target.value === 'all' ? null : e.target.value, page: null })}>
          <option value="all">Qualquer situação</option>
          <option value="active">Ativos</option>
          <option value="paused">Pausados</option>
          <option value="closed">Finalizados</option>
          <option value="moderated">Moderados (proibido, em revisão…)</option>
          <option value="other">Outras</option>
        </select>
        <select className="select" aria-label="Vínculo" value={linkValue} onChange={(e) => set({ link: e.target.value === (review ? 'review' : 'all') ? null : e.target.value, page: null })}>
          <option value="all">Qualquer vínculo</option>
          <option value="review">Para revisar</option>
          <option value="linked">Vinculados</option>
          <option value="ambiguous">Ambíguos</option>
          <option value="orphan">Órfãos</option>
        </select>
        <select className="select" aria-label="Catálogo" value={params.get('catalog') ?? 'all'} onChange={(e) => set({ catalog: e.target.value === 'all' ? null : e.target.value, page: null })}>
          <option value="all">Catálogo ML: todos</option>
          <option value="true">Só de catálogo</option>
          <option value="false">Fora do catálogo</option>
        </select>
        <select className="select" aria-label="Duplicados" value={params.get('dup') ?? ''} onChange={(e) => set({ dup: e.target.value || null, page: null })}>
          <option value="">Duplicados: todos</option>
          <option value="any">Duplicados (mesmo SKU e tipo)</option>
          <option value="same_type">Duplicados, catálogo e tradicional à parte</option>
        </select>
        {params.get('dup') && canEdit && account && <DuplicatesButton accountId={account.id} catalogApart={params.get('dup') === 'same_type'} />}
        <select className="select" aria-label="Origem" value={params.get('origin') ?? 'all'} onChange={(e) => set({ origin: e.target.value === 'all' ? null : e.target.value, page: null })}>
          <option value="all">Qualquer origem</option>
          <option value="berytus">Criados pela Berytus</option>
        </select>
        <select className="select" aria-label="Estoque" value={params.get('stock') ?? 'all'} onChange={(e) => set({ stock: e.target.value === 'all' ? null : e.target.value, page: null })}>
          <option value="all">Qualquer estoque</option>
          <option value="in">Com estoque</option>
          <option value="out">Sem estoque</option>
        </select>
        <select className="select" aria-label="Ordenar" value={params.get('sort') ?? 'default'} onChange={(e) => set({ sort: e.target.value === 'default' ? null : e.target.value, page: null })}>
          <option value="default">Ordem: revisar primeiro</option>
          <option value="stock_desc">Maior estoque</option>
          <option value="stock_asc">Menor estoque</option>
          <option value="price_desc">Maior preço</option>
          <option value="price_asc">Menor preço</option>
        </select>
        <select className="select" aria-label="Alertas" value={params.get('alert') ?? ''} onChange={(e) => set({ alert: e.target.value || null, page: null })}>
          <option value="">Qualquer alerta</option>
          <option value="any">Com algum alerta</option>
          <option value="none">Sem nenhum alerta</option>
          {(Object.keys(LISTING_ALERT) as ListingAlert[]).map((k) => <option key={k} value={k}>{LISTING_ALERT[k].label}</option>)}
        </select>
        <span className="grow" />
        <a className="btn" href={`/api/v1/exports/listings.csv?channel=mercadolivre&status=${['active', 'paused'].includes(params.get('status') ?? '') ? params.get('status') : (params.get('status') ? 'all' : 'active')}${categoryParams(params)}`}
          title="Planilha (abre no Excel): SKU, título, MLB, preço, estoque e título no Mercado Livre. Sem filtro de situação: só os ativos.">
          <Download size={16} /> Exportar planilha
        </a>
        {account && (
          <button className="btn" type="button" disabled={pull.isPending || pull.isSuccess} onClick={() => pull.mutate()}
            title="Lê todos os anúncios da conta no Mercado Livre (situação, preço, estoque). Anúncios mexidos no ML já entram sozinhos a cada 10 minutos.">
            <RefreshCw size={16} /> {pull.isPending ? 'Enviando…' : pull.isSuccess ? 'Leitura em andamento' : 'Atualizar do Mercado Livre'}
          </button>
        )}
      </div>
      {account && params.get('brand') && (
        <BrandActionBar accountId={account.id} brand={params.get('brand')!} canEdit={canEdit}
          onDone={() => qc.invalidateQueries({ queryKey: ['meli'] })} />
      )}
      {(link.error || bulk.error || pull.error) && <div style={{ marginBottom: 12 }}><ErrorBox error={link.error ?? bulk.error ?? pull.error} /></div>}
      {bulk.data && bulk.data.failed.length > 0 && (
        <div className="notice err small" style={{ marginBottom: 12 }}>
          {int(bulk.data.ok)} alterado(s); {int(bulk.data.failed.length)} recusado(s) pelo Mercado Livre:
          {bulk.data.failed.slice(0, 5).map((f) => <div key={f.externalId}>{f.externalId}: {f.error}</div>)}
        </div>
      )}

      <div className="panel flush">
        {canAll && (
          <div className="bulkbar" role="group" aria-label="Todos do filtro">
            <span><b>{int(total)}</b> anúncio{total === 1 ? '' : 's'} {params.get('status') === 'paused' ? 'pausado' : 'ativo'}{total === 1 ? '' : 's'} neste filtro{params.get('alert') === 'none' ? ', sem nenhum alerta' : ''}:</span>
            {params.get('status') === 'paused'
              ? <button className="btn sm primary" type="button" disabled={bulkAll.isPending}
                  onClick={() => confirm(`Ativar os ${total} anúncios deste filtro no Mercado Livre?`) && bulkAll.mutate('active')}>Ativar todos do filtro</button>
              : <button className="btn sm" type="button" disabled={bulkAll.isPending}
                  onClick={() => confirm(`Pausar os ${total} anúncios deste filtro no Mercado Livre?`) && bulkAll.mutate('paused')}>Pausar todos do filtro</button>}
            {bulkAll.data && <span className="small">{int(bulkAll.data.queued)} enviados para a fila. Os recusados aparecem no Envio dos anúncios, com o motivo.</span>}
            {bulkAll.error && <span className="small" style={{ color: 'var(--err)' }}>{(bulkAll.error as Error).message}</span>}
          </div>
        )}
        {selected.size > 0 && canEdit && (
          <div className="bulkbar" role="status">
            <b>{int(selected.size)} selecionado(s)</b>
            <button className="btn sm" type="button" disabled={bulk.isPending} onClick={() => bulk.mutate('paused')}>Pausar</button>
            <button className="btn sm" type="button" disabled={bulk.isPending} onClick={() => bulk.mutate('active')}>Ativar</button>
            <button className="btn sm quiet" type="button" style={{ color: 'var(--err)' }} disabled={bulk.isPending}
              onClick={() => confirm(`Finalizar ${selected.size} anúncio(s)? Finalizado não volta a vender: para vender de novo, é preciso republicar.`) && bulk.mutate('closed')}>Finalizar</button>
            <button className="btn sm quiet" type="button" style={{ color: 'var(--err)' }} disabled={bulk.isPending}
              onClick={() => confirm(`Excluir ${selected.size} anúncio(s) no Mercado Livre? Eles são finalizados e somem do seu painel do Mercado Livre. Não tem volta.`) && bulk.mutate('deleted')}>Excluir</button>
            {bulk.isPending && <span className="small">Enviando ao Mercado Livre…</span>}
            <span style={{ flex: 1 }} />
            <button className="btn sm quiet" type="button" onClick={() => setSelected(new Set())}><X size={14} /> Limpar</button>
          </div>
        )}
        {list.error ? <div style={{ padding: 18 }}><ErrorBox error={list.error} /></div>
          : !list.isPending && rows.length === 0 ? (
            <div className="empty">
              <b>{review ? 'Nada para revisar' : 'Nenhum anúncio encontrado'}</b>
              {review ? 'Todos os anúncios lidos estão vinculados a um produto.' : 'Mude a busca ou os filtros, ou faça a leitura dos anúncios no resumo do canal.'}
            </div>
          ) : (
            <>
              <div className="grid-wrap" style={{ opacity: list.isPlaceholderData ? 0.55 : 1 }}>
                <table className="grid" style={{ width: '100%', minWidth: minWidth(LISTING_COLS) }}>
                  <colgroup>{LISTING_COLS.map(([, w], i) => <col key={i} style={w ? { width: w } : undefined} />)}</colgroup>
                  <thead><tr>{LISTING_COLS.map(([l, , c], i) => <th key={i} className={c}>{i === 0 ? (
                    canEdit && <input type="checkbox" className="check" aria-label="Selecionar a página" checked={allOnPage}
                      onChange={() => setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r.id)))} />
                  ) : l}</th>)}</tr></thead>
                  <tbody>
                    {list.isPending
                      ? Array.from({ length: 10 }, (_, i) => <tr key={i}>{LISTING_COLS.map((_, j) => <td key={j}><div className="skeleton" /></td>)}</tr>)
                      : rows.map((l) => (
                          <tr key={l.id} style={{ cursor: 'default' }} className={selected.has(l.id) ? 'selected' : undefined}>
                            <td>{canEdit && <input type="checkbox" className="check" aria-label={`Selecionar ${l.externalId}`} checked={selected.has(l.id)} onChange={() => toggle(l.id)} />}</td>
                            <td>
                              <button className="icon-btn" type="button" onClick={() => setEditing(l.id)} aria-label={`${canEdit ? 'Editar' : 'Ver'} ${l.externalId}`} title={canEdit ? 'Editar anúncio' : 'Ver anúncio'}>
                                <Pencil size={16} />
                              </button>
                            </td>
                            <td><Thumb src={l.thumbnail} alt="" /></td>
                            <td>
                              <div className="cell-2">
                                <span title={l.title ?? undefined}>{l.title}</span>
                                <span className="sub">
                                  {l.permalink ? <a className="ext" href={l.permalink} target="_blank" rel="noreferrer">{l.externalId} <ExternalLink size={11} /></a> : l.externalId}
                                  {l.variation && <> · {l.variation}</>}
                                  {l.unitsPerPack && l.unitsPerPack > 1 && <> · {l.unitsPerPack} unidades</>}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="cell-2">
                                <span className="code">{l.sellerSku ?? '—'}</span>
                                {l.gtin && <span className="sub">{l.gtin}</span>}
                              </div>
                            </td>
                            <td>
                              <div className="cell-2">
                                <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{statusTag(l.status)}{subTags(l.subStatus)}</span>
                                {l.isCatalog && <span className="sub">catálogo</span>}
                              </div>
                            </td>
                            <td className="num">
                              <div className="cell-2" style={{ alignItems: 'flex-end' }}>
                                <span>{brl(l.price)}</span>
                                <span className="sub">
                                  {[
                                    l.marginPct != null ? `margem ${l.marginPct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : null,
                                    l.berytusPrice && l.alerts.includes('price_out_of_sync') ? `tabela ${brl(l.berytusPrice)}` : null,
                                  ].filter(Boolean).join(' · ')}
                                </span>
                              </div>
                            </td>
                            <td className="num">{l.availableQuantity != null ? int(l.availableQuantity) : '—'}</td>
                            <td className="num" style={l.alerts.includes('stock_out_of_sync') ? { color: 'var(--warn)', fontWeight: 600 } : undefined}
                              title="Estoque que a Berytus manda para o Mercado Livre (tabela de preço e estoque). Em laranja: diferente do anúncio.">
                              {l.berytusStock != null ? int(l.berytusStock) : '—'}
                            </td>
                            <td>
                              {l.buyBox ? (
                                <div className="cell-2">
                                  <span>{buyBoxTag(l.buyBox.status)} {l.buyBox.priceToWin && <b className="tnum">{brl(l.buyBox.priceToWin)}</b>}</span>
                                  {l.buyBox.priceToWin && (
                                    <span className="sub" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                      margem {l.buyBox.marginPct != null ? `${l.buyBox.marginPct.toLocaleString('pt-BR')}%` : '—'}
                                      {l.product && l.buyBox.status !== 'winning' && (
                                        <button className="btn sm" style={{ height: 22, padding: '0 8px', fontSize: 12 }} type="button" disabled={applyBuyBox.isPending}
                                          onClick={() => applyBuyBox.mutate(l.id)} title="Usa o preço para ganhar como preço na mão do produto vinculado">
                                          Aplicar
                                        </button>
                                      )}
                                    </span>
                                  )}
                                </div>
                              ) : <span className="small muted">{l.isCatalog ? 'sem leitura' : '—'}</span>}
                            </td>
                            <td className="wrap">
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {l.alerts.map((a) => (
                                  <span key={a} title={TITLE_ALERTS.has(a) ? l.titleAlertDetail ?? undefined : undefined}>
                                    <Tag tone={LISTING_ALERT[a].tone}>{LISTING_ALERT[a].label}</Tag>
                                  </span>
                                ))}
                              </div>
                              {l.alerts.some((a) => TITLE_ALERTS.has(a)) && (
                                <div className="small muted" style={{ marginTop: 4, maxWidth: 220 }} title={l.titleAlertDetail ?? undefined}>
                                  {l.product && <>Ligado a: <b>{l.product.title ?? l.product.sku}</b></>}
                                  {canEdit && (
                                    <button type="button" className="btn sm quiet" style={{ marginLeft: 4 }} disabled={titleOk.isPending}
                                      title="O anúncio está certo: não alertar mais (volta a alertar se um dos títulos mudar)" onClick={() => titleOk.mutate(l.id)}>
                                      Está certo
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="cell-2">
                                <span><Tag tone={LINK[l.linkStatus].tone}>{LINK[l.linkStatus].label}</Tag></span>
                                {l.linkMethod && <span className="sub">{METHOD[l.linkMethod]}</span>}
                              </div>
                            </td>
                            <td className="wrap">
                              <ProductCell l={l} busy={link.isPending} onLink={(productId) => link.mutate({ id: l.id, productId })} onPick={() => setPicking(l)} />
                            </td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
              <div className="cards">
                {rows.map((l) => (
                  <div key={l.id} className="card-row" onClick={() => setEditing(l.id)} style={{ cursor: 'pointer' }}>
                    <Thumb src={l.thumbnail} alt="" />
                    <div style={{ minWidth: 0 }}>
                      <div className="t">{l.title}</div>
                      <div className="m">{l.externalId}{l.sellerSku && <> · SKU {l.sellerSku}</>}</div>
                      <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {statusTag(l.status)} {subTags(l.subStatus)} <Tag tone={LINK[l.linkStatus].tone}>{LINK[l.linkStatus].label}</Tag>
                      </div>
                      {l.product && <div className="m" style={{ marginTop: 4 }}>→ {l.product.sku} · {titleOf(l.product)}</div>}
                      {l.linkNote && <div className="m" style={{ marginTop: 4 }}>{l.linkNote}</div>}
                    </div>
                    <div className="r">
                      <div>{brl(l.price)}</div>
                      <div className="muted">{l.availableQuantity ?? '—'} un.</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        {list.data && <Pager total={list.data.total} page={page} pageSize={list.data.pageSize} onPage={(n) => set({ page: n > 1 ? String(n) : null })} />}
      </div>

      {editing && <ListingEditor listingId={editing} onClose={() => setEditing(null)} />}
      <PickProduct
        listing={picking}
        onClose={() => setPicking(null)}
        onPick={(productId) => {
          link.mutate({ id: picking!.id, productId });
          setPicking(null);
        }}
      />
    </>
  );
}

function ProductCell({ l, busy, onLink, onPick }: { l: ChannelListingRow; busy: boolean; onLink: (id: string | null) => void; onPick: () => void }) {
  if (l.product) {
    return (
      <div className="cell-2">
        <span title={l.product.title ?? undefined}>
          <Link to={`/catalogo/${l.product.id}`}>{titleOf(l.product)}</Link>
          {l.product.isKit && <> <Tag tone="info">Kit</Tag></>}
        </span>
        <span className="sub">
          SKU {l.product.sku}
          {' · '}
          <button className="btn quiet sm" style={{ height: 20, padding: '0 4px', fontSize: 12 }} type="button" disabled={busy} onClick={onPick}>trocar</button>
          {l.linkMethod === 'manual' && (
            <button className="btn quiet sm" style={{ height: 20, padding: '0 4px', fontSize: 12 }} type="button" disabled={busy} onClick={() => onLink(null)} title="Desfaz o vínculo manual; a próxima conciliação tenta de novo pelo SKU e EAN">
              <Unlink size={11} /> desfazer
            </button>
          )}
        </span>
      </div>
    );
  }
  return (
    <div className="cell-2">
      {l.suggestion ? (
        <span title={`Título parecido (${Math.round(l.suggestion.score * 100)}%)`}>
          <button className="btn sm" type="button" disabled={busy}
            onClick={() => (!l.suggestion!.warning || confirm(`${l.suggestion!.warning}\n\nLigar mesmo assim?`)) && onLink(l.suggestion!.id)}>
            <Link2 size={13} /> {l.suggestion.sku}
          </button>{' '}
          <span className="small">{titleOf(l.suggestion)}</span>
          {l.suggestion.warning && <div className="small" style={{ color: 'var(--err)' }}>⚠ {l.suggestion.warning.replace('o produto ligado', 'a sugestão')}</div>}
        </span>
      ) : (
        <span className="small muted">{l.linkNote ?? 'Sem produto'}</span>
      )}
      <span className="sub">
        {l.suggestion && l.linkNote && <>{l.linkNote} · </>}
        <button className="btn quiet sm" style={{ height: 20, padding: '0 4px', fontSize: 12 }} type="button" disabled={busy} onClick={onPick}>escolher produto</button>
      </span>
    </div>
  );
}

/** Busca no catálogo para vincular o anúncio a um produto. */
function PickProduct({ listing, onClose, onPick }: { listing: ChannelListingRow | null; onClose: () => void; onPick: (id: string) => void }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    if (listing) setQ(listing.sellerSku ?? listing.gtin?.replace(/\D/g, '') ?? (listing.title ?? '').split(' ').slice(0, 3).join(' '));
  }, [listing]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const res = useQuery({
    queryKey: ['pick', debounced],
    queryFn: () => api<Page<ProductRow>>(`/catalog/products?q=${encodeURIComponent(debounced)}&pageSize=8&active=all`),
    enabled: !!listing && debounced.length >= 2,
  });

  return (
    <Dialog.Root open={!!listing} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="panel" style={{ position: 'fixed', top: '12vh', left: '50%', transform: 'translateX(-50%)', width: 'min(560px, calc(100vw - 32px))', zIndex: 60 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Dialog.Title asChild><h2 style={{ margin: 0 }}>Vincular anúncio a um produto</h2></Dialog.Title>
            <span style={{ flex: 1 }} />
            <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
          </div>
          <Dialog.Description className="small muted" style={{ marginTop: 0 }}>
            {listing?.externalId} · {listing?.title}
          </Dialog.Description>
          <label className="search" style={{ width: '100%', marginBottom: 10 }}>
            <Search size={16} />
            <input className="input" autoFocus placeholder="SKU, EAN ou nome" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          {res.isFetching && !res.data ? <Loading /> : res.data?.items.length === 0 ? (
            <p className="small muted">Nenhum produto com essa busca.</p>
          ) : (
            <div style={{ display: 'grid', gap: 4, maxHeight: '46vh', overflow: 'auto' }}>
              {res.data?.items.map((p) => (
                <button key={p.id} type="button" className="btn" style={{ justifyContent: 'flex-start', height: 'auto', padding: '8px 10px', textAlign: 'left' }} onClick={() => onPick(p.id)}>
                  <span style={{ display: 'grid', minWidth: 0 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleOf(p)}{p.isKit && ` (kit ${p.kitUnits} un.)`}</span>
                    <span className="small muted">SKU {p.sku}{p.gtin && ` · ${p.gtin}`}{!p.active && ' · inativo'}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Conta do Mercado Livre do cliente (a primeira conectada). */
/** Canal da aba aberta: código, nome e caminho base das telas. */
export function useChannelTab() {
  const location = useLocation();
  if (location.pathname.startsWith('/canal/shopee')) return { code: 'shopee' as const, name: 'Shopee', base: '/canal/shopee', na: 'na Shopee' };
  if (location.pathname.startsWith('/canal/ifood')) return { code: 'ifood' as const, name: 'iFood', base: '/canal/ifood', na: 'no iFood' };
  return { code: 'mercadolivre' as const, name: 'Mercado Livre', base: '/canal/mercadolivre', na: 'no Mercado Livre' };
}

/** Conta do canal da aba aberta (Mercado Livre ou Shopee): as telas de preço e configurações servem às duas. */
export function useMeliAccount() {
  const channel = useChannelTab().code;
  const q = useQuery({ queryKey: ['channels'], queryFn: () => api<ChannelInfo[]>('/channels') });
  const selected = useSelectedAccountId(channel);
  const account = pickAccount(q.data?.find((c) => c.code === channel)?.accounts, selected);
  return { account, channel, isPending: q.isPending, error: q.error };
}
